import React from 'react';
import { RiCheckLine, RiCheckboxCircleFill, RiCloseCircleFill, RiTrophyLine, RiMedalLine, RiTeamLine, RiBuildingLine, RiDashboard3Line, RiInformationLine, RiMenuAddLine } from '@remixicon/react';
import { Card } from '@components/ui/Card';
import { Root as Button } from '@components/ui/Button';
import { Checkbox } from '@components/ui/Checkbox';
import { TrustScore } from '@/features/trust-score';
import ImageCarousel from '@components/ui/ImageCarousel';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, ACTIONS, ROLES, normalizeRole } from '@/lib/permissions';

const TailCard = ({ 
  id,
  registration,
  model,
  year,
  passengers,
  range,
  operator,
  location,
  trustScore,
  trustScoreNum,
  status,
  badges,
  image,
  onDetails,
  isSelected,
  onSelect,
  features,
  addtails = false
}) => {

  const { can, role } = usePermissions();

  // Shortlist / Add Tail kicks off the quote-building workflow, so requires
  // full CRUD on Booking & Quoting (admin + broker only).
  const canShortlistTail =
    can(MODULES.BOOKING, ACTIONS.CREATE) &&
    can(MODULES.BOOKING, ACTIONS.UPDATE);

  const isFinance = normalizeRole(role) === ROLES.FINANCE;
    
  const handleShortlist = () => {
    onSelect?.(id, !isSelected);
  };

  const handleSelect = (checked) => {
    onSelect?.(id, checked);
  };

  return (
    <Card className="relative overflow-hidden hover:shadow-md transition-shadow duration-200 border-0 !rounded-3xl">
    
      {canShortlistTail && (
        <div className="absolute top-3 left-3 z-10 rounded p-1 shadow-sm">
          <Checkbox
            checked={isSelected}
            onCheckedChange={handleSelect}
            className="border-gray-300 bg-white"
          />
        </div>
      )}

      {/* Aircraft Image Carousel */}
      <div className="relative bg-gray-50">
        <ImageCarousel
          images={Array.isArray(image) ? image : [image]}
          alt={`${registration} - ${model}`}
          className="h-[350px]"
        />
      </div>

      {/* Card Content */}
      <div className="p-4 pt-0 border border-b-0 border-gray-100 flex flex-col justify-between">
        {/* Registration and Status */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <h3 className="text-2xl text-gray-900">{registration}</h3>
              <div className={`flex items-center gap-1 text-gray-600`}>
                <span className="border border-gray-200 px-2 !rounded-lg flex items-center gap-1 text-sm">
                  {
                    !status || status=="Unavailable" ? <RiCloseCircleFill className="w-4 h-4 text-red-500" /> :<RiCheckboxCircleFill className="w-4 h-4 text-green-400" /> 
                  }
                  <span className="capitalize">{status?.toLowerCase()}</span></span>
              </div>
          </div>
          {/* Trust Score aligned with title */}
          <div className="flex items-center">
            <TrustScore score={trustScore} numericScore={trustScoreNum || 0} size="sm" />
          </div>
        </div>

        {/* Aircraft Details */}
        <div className="text-sm">
          <div className="flex text-gray-700">
            <span className="text-gray-500">{model}</span> {year && year !== "-" && <> • <span>{year}</span></>}
             <span className="ml-auto"><TrustScore score={trustScore} numericScore={null} size="sm" showBadge /></span>
          </div>
          <div className="flex items-center gap-1 text-gray-500 mt-3">
           <RiTeamLine className="w-4 h-4" /> {passengers ? <><span>Up to {passengers} </span> passengers</> : "-"}
          </div>
          <div className="flex items-center gap-1 text-gray-500 mt-1">
            <RiDashboard3Line className="w-4 h-4" /> {range && range!="-" ? <><span>Range: {range} </span></> : "-"}
          </div>
          <div className="flex items-center gap-1 text-gray-500 mt-1">
            <RiBuildingLine className="w-4 h-4" />
            <span>{location}</span>
          </div>
        </div>

        {((operator?.argus_rating && operator?.argus_rating !== "No") || (operator?.wyvern_rating && operator?.wyvern_rating !== "No") || features?.no_aog) && (
        <div className="flex flex-wrap gap-2 mt-3">

          {operator?.argus_rating && operator?.argus_rating!="No" &&
          <span className={`flex px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-2xl font-medium`}>
            <RiTrophyLine className="h-4 w-4 text-gray-600"/> {operator?.argus_rating}
          </span>}

          {operator?.wyvern_rating && operator?.wyvern_rating!="No" &&
          <span className={`flex px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-2xl font-medium`}>
            <RiMedalLine className="h-4 w-4 text-gray-600"/> WINGMAN
          </span>
          }

          {features?.no_aog &&
          <span className={`flex px-2 py-1 bg-green-100 text-green-700 text-xs rounded-2xl font-medium`}>
            <RiCheckLine className="h-4 w-4 text-gray-600"/> NO AOG
          </span>
          }

        </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 mt-3">
          {!isFinance && (
            <Button
              variant="secondary"
              mode="stroke"
              size="small"
              className="flex-1 rounded-2xl py-2 border-gray-200"
              onClick={() => onDetails?.(id)}
            >
               <RiInformationLine className="h-4 w-4 text-gray-600"/> Details
            </Button>
          )}
          {canShortlistTail && (
            <Button
              variant="primary"
              mode="filled"
              size="small"
              className="flex-1 text-primary-500 rounded-2xl !bg-accent py-2"
              onClick={handleShortlist}
            >
               <RiMenuAddLine className="h-4 w-4 text-primary-500"/> {addtails ? 'Add Tail' : 'Shortlist'}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default TailCard;
